import React, { useEffect, useRef } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import Offer from 'components/offers/Offer';
import MobileOfferCard from 'components/offers/MobileOfferCard';
import { listingType } from 'components/offers/utils';

const ListView = ({ 
  offers = [], 
  highlightedOfferId, 
  onOfferHover,
  displayMode = 'card'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Changed to 'md' for mobile-only
  const offerRefs = useRef({});
  
  // On mobile, always show cards (like Popular offers)
  const showCardsOnMobile = isMobile;
  const offerTypeToUse = showCardsOnMobile 
    ? listingType.card 
    : (displayMode === 'card' ? listingType.card : listingType.list);

  useEffect(() => {
    if (highlightedOfferId && offerRefs.current[highlightedOfferId]) {
      // Scroll logic intentionally disabled
    }
  }, [highlightedOfferId]);

  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: showCardsOnMobile ? 'none' : '600px',
        overflowY: showCardsOnMobile ? 'visible' : 'auto',
        overflowX: 'hidden', // Prevent horizontal scrolling
        px: showCardsOnMobile ? 0 : undefined, // No padding on mobile, handled by Container
        pr: showCardsOnMobile ? undefined : 1,
        pl: showCardsOnMobile ? undefined : 2, // added left padding so badge is not clipped
        pt: showCardsOnMobile ? 0 : 2, // added top padding so first badge is not clipped
        pb: showCardsOnMobile ? 2 : 0,
        boxSizing: 'border-box',
        margin: 0,
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
        '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
      }}
    >
      {showCardsOnMobile ? (
        // Mobile: Use MobileOfferCard in a single column layout
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            boxSizing: 'border-box',
          }}
        >
          {offers.map((offer) => (
            <Box
              key={offer.id}
              ref={(el) => (offerRefs.current[offer.id] = el)}
              onMouseEnter={() => onOfferHover && onOfferHover(offer.id)}
              onMouseLeave={() => onOfferHover && onOfferHover(null)}
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                margin: 0,
                padding: 0,
              }}
            >
              <MobileOfferCard
                {...offer}
                revalidateList={null}
              />
            </Box>
          ))}
        </Box>
      ) : (
        // Desktop: Use existing Offer component
        <Grid 
          container 
          direction="column" 
          spacing={1} 
          alignItems="stretch"
        >
          {offers.map((offer, index) => (
            (() => {
              const badgeIndex = offer.displayIndex ?? index + 1;
              return (
            <Grid
              key={offer.id}
              item
              xs={12}
              ref={(el) => (offerRefs.current[offer.id] = el)}
              onMouseEnter={() => onOfferHover && onOfferHover(offer.id)}
              onMouseLeave={() => onOfferHover && onOfferHover(null)}
              sx={{
                transition: 'transform 0.2s ease',
                transform: highlightedOfferId === offer.id ? 'scale(1.02)' : 'scale(1)',
                boxShadow: 'none',
                borderRadius: '6px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  '&::before': highlightedOfferId === offer.id ? {
                    content: `"${badgeIndex}"`,
                    position: 'absolute',
                    top: -6,
                    left: -6,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    zIndex: 1,
                  } : {},
                }}
              >
                <Offer
                  offer={offer}
                  offerOrigin="search"
                  offerType={offerTypeToUse}
                />
              </Box>
            </Grid>
              );
            })()
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ListView;
